/**
 * Where the day and week strips scroll to (Scores UX spec v1.0 section 4.2):
 * the selected chip sits SECOND from the left, so the fan sees one chip of
 * context before it, and the strip's own leading padding keeps the first chip
 * from ever being clipped. Pure geometry plus one DOM call, so the rule is
 * testable without a layout engine.
 */

/** The scrollLeft that puts the chip before `chip` at the strip's leading
 *  edge, inside the strip's leading padding. 0 for the first chip. */
export function scrollLeftForSecondSlot(input: {
  previousChipOffsetLeft: number | null;
  leadingPaddingPx: number;
}): number {
  if (input.previousChipOffsetLeft === null) return 0;
  return Math.max(0, input.previousChipOffsetLeft - input.leadingPaddingPx);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Scrolls `strip` so `chip` sits second from the left. The first scroll after
 * mount jumps; a later one glides, unless the fan asked for reduced motion.
 * Only the strip's own scrollLeft moves, never the page.
 */
export function scrollStripToChip(input: {
  strip: HTMLElement;
  chip: HTMLElement;
  isInitial: boolean;
}): void {
  const previous = input.chip.previousElementSibling;
  const leadingPaddingPx =
    Number.parseFloat(getComputedStyle(input.strip).paddingLeft) || 0;
  const left = scrollLeftForSecondSlot({
    previousChipOffsetLeft:
      previous instanceof HTMLElement ? previous.offsetLeft : null,
    leadingPaddingPx,
  });
  const behavior: ScrollBehavior =
    input.isInitial || prefersReducedMotion() ? "auto" : "smooth";
  if (typeof input.strip.scrollTo === "function") {
    input.strip.scrollTo({ left, behavior });
  } else {
    input.strip.scrollLeft = left;
  }
}
